// The Sigil-Cast: render the commit hash SHA256(server_seed ‖ weight_table_hash) as a
// deterministic wax-seal glyph — "your fate is sealed." A symmetric identicon inside a
// wax-seal ring, in Molten Gold on stone. When a spin's recompute passes, the seal
// "cracks" in Arcane Green.
//
// IMPORTANT: the sigil is DECORATION. The proof is the SubtleCrypto recompute in
// fairness.ts. Never present the pretty glyph as "why it's fair" — that is exactly the
// security theater the ethos rejects. It is a mnemonic for a hash, nothing more.

const SVG = 'http://www.w3.org/2000/svg';

const GOLD = '#F5B301';
const GOLD_LO = '#C8860A';
const GREEN = '#39E58C';
const STONE = '#1E2024';

export interface SigilOptions {
  size?: number;
  cracked?: boolean;
}

export function renderSigil(commitHash: string, opts: SigilOptions = {}): SVGSVGElement {
  const size = opts.size ?? 96;
  const svg = document.createElementNS(SVG, 'svg');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('viewBox', '0 0 100 100');
  svg.setAttribute('role', 'img');
  svg.setAttribute('aria-label', `commit sigil ${commitHash.slice(0, 8)}`);

  const bytes = hexBytes(commitHash);

  // Wax-seal ring.
  const ring = circle(50, 50, 47, 'none', opts.cracked ? GREEN : GOLD_LO, 3);
  svg.appendChild(circle(50, 50, 47, STONE, 'none', 0));
  svg.appendChild(ring);
  svg.appendChild(circle(50, 50, 41, 'none', opts.cracked ? GREEN : GOLD, 1));

  // Symmetric 5x5 identicon (left 3 cols mirrored), cells 14px inside a 70px field.
  const cell = 14;
  const originX = 15;
  const originY = 15;
  for (let col = 0; col < 3; col++) {
    for (let row = 0; row < 5; row++) {
      const idx = col * 5 + row;
      if ((bytes[idx % bytes.length] >> (idx % 8)) & 1) {
        addCell(svg, originX + col * cell, originY + row * cell, cell, GOLD);
        if (col < 2) {
          addCell(svg, originX + (4 - col) * cell, originY + row * cell, cell, GOLD);
        }
      }
    }
  }

  if (opts.cracked) {
    // A single green fracture across the seal.
    const crack = document.createElementNS(SVG, 'path');
    crack.setAttribute('d', 'M20 30 L45 52 L38 62 L62 78');
    crack.setAttribute('fill', 'none');
    crack.setAttribute('stroke', GREEN);
    crack.setAttribute('stroke-width', '2.5');
    crack.setAttribute('stroke-linecap', 'round');
    crack.setAttribute('stroke-linejoin', 'round');
    svg.appendChild(crack);
  }

  return svg;
}

function addCell(svg: SVGSVGElement, x: number, y: number, s: number, fill: string): void {
  const rect = document.createElementNS(SVG, 'rect');
  rect.setAttribute('x', String(x));
  rect.setAttribute('y', String(y));
  rect.setAttribute('width', String(s));
  rect.setAttribute('height', String(s));
  rect.setAttribute('fill', fill);
  rect.setAttribute('rx', '2');
  svg.appendChild(rect);
}

function circle(
  cx: number,
  cy: number,
  r: number,
  fill: string,
  stroke: string,
  strokeWidth: number,
): SVGCircleElement {
  const c = document.createElementNS(SVG, 'circle');
  c.setAttribute('cx', String(cx));
  c.setAttribute('cy', String(cy));
  c.setAttribute('r', String(r));
  c.setAttribute('fill', fill);
  if (stroke !== 'none') {
    c.setAttribute('stroke', stroke);
    c.setAttribute('stroke-width', String(strokeWidth));
  }
  return c;
}

function hexBytes(hex: string): number[] {
  const out: number[] = [];
  for (let i = 0; i + 1 < hex.length; i += 2) out.push(parseInt(hex.slice(i, i + 2), 16));
  return out.length ? out : [0];
}
